import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../../ui/dropdown-menu";
import MoveToFolderMenu from "./MoveToFolderMenu";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import {
  Play,
  Pencil,
  Eye,
  MoreVertical,
  Star,
  PenLine,
  Trash2,
  Archive,
  Bell,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Link,
  ClipboardList,
  Lock,
  UserPlus,
  Download,
  Upload,
  Table2,
  BarChart3,
  BookOpen,
  Lightbulb,
  MoreHorizontal,
  FolderInput,
} from "lucide-react";
import { Badge } from "../../ui/badge";
import useAuth from "../../../hooks/useAuth";
import useProcess from "../../../hooks/useProcess";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { DateTime } from "luxon";

type SortColumn = "name" | "status" | "engagements" | "recent" | "folder";
type SortDir = "asc" | "desc";

interface ActivityListViewProps {
  prescriptions: any[];
  editPrescription: (idOrIndex: any) => void;
  viewPrescription: (idOrIndex: any, isMine: boolean) => void;
  renamePrescription: (idOrIndex: any) => void;
  deletePrescription: (idOrIndex: any) => void;
  copyPrescriptionAsExample: (idOrIndex: any) => void;
  sharePrescription: (idOrIndex: any) => void;
  managePermissions: (idOrIndex: any) => void;
  expertShareWorkflow: (idOrIndex: any) => void;
  exportPrescription: (idOrIndex: any) => void;
  onMoveToFolder?: (prescriptionId: string, folderId: string | null) => void;
  onToggleFavorite?: (prescriptionId: string) => void;
  folders: any[];
  refreshLists: () => void;
  isMobile?: boolean;
}

const ActivityListView: React.FC<ActivityListViewProps> = ({
  prescriptions,
  editPrescription,
  viewPrescription,
  renamePrescription,
  deletePrescription,
  copyPrescriptionAsExample,
  sharePrescription,
  managePermissions,
  expertShareWorkflow,
  exportPrescription,
  onMoveToFolder,
  onToggleFavorite,
  folders,
  refreshLists,
  isMobile = false,
}) => {
  const { t, i18n } = useTranslation();
  const { auth, user } = useAuth();
  const { startProcess, resumeProcess } = useProcess();
  const location = useLocation();
  const navigate = useNavigate();

  const handleCopyLink = async (prescriptionId: string) => {
    try {
      const activityUrl = `${window.location.origin}/share/activity?wid=${prescriptionId}`;
      await navigator.clipboard.writeText(activityUrl);
      toast.success(t("msg_link_copied_to_clipboard") || "Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error(t("failed_to_copy_link") || "Failed to copy link");
    }
  };

  const [sortCol, setSortCol] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir(col === "name" || col === "folder" ? "asc" : "desc");
    }
  };

  const getRunningCount = (p: any) =>
    Array.isArray(p.processes) ? p.processes.filter((pr: any) => pr.status === "RUNNING").length : 0;

  const getStatusOrder = (p: any) => {
    const running = getRunningCount(p);
    if (running > 0) return 0;
    if (p.engagementCount > 0) return 1;
    return 2;
  };

  const getLatestTs = (p: any): number => {
    if (!Array.isArray(p.processes) || p.processes.length === 0) return 0;
    let latest = 0;
    for (const proc of p.processes) {
      const ts = proc.lastUpdated || proc.started || 0;
      if (ts > latest) latest = ts;
    }
    return latest;
  };

  const sortedPrescriptions = useMemo(() => {
    if (!sortCol) return prescriptions;
    const sorted = [...prescriptions].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case "name":
          cmp = (a.name || "").localeCompare(b.name || "", i18n.language);
          break;
        case "status":
          cmp = getStatusOrder(a) - getStatusOrder(b);
          break;
        case "engagements":
          cmp = (a.engagementCount || 0) - (b.engagementCount || 0);
          break;
        case "recent":
          cmp = getLatestTs(a) - getLatestTs(b);
          break;
        case "folder":
          cmp = (a.metadata?.folderName || "").localeCompare(b.metadata?.folderName || "", i18n.language);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [prescriptions, sortCol, sortDir, i18n.language]);

  const handleStartOrResume = async (prescription: any) => {
    if (!prescription.valid) return;

    const runningProcessCount = Array.isArray(prescription.processes)
      ? prescription.processes.filter((p: any) => p.status === "RUNNING").length
      : 0;

    if (
      runningProcessCount > 0 &&
      prescription?.settings?.engagementMode === "same"
    ) {
      const latestProcess = prescription.processes.find(
        (p: any) => p.status === "RUNNING"
      );
      if (latestProcess) {
        let processId = latestProcess._id;
        if (latestProcess.pendingInnerProcess) {
          processId = latestProcess.pendingInnerProcess;
        }
        await resumeProcess({
          processId,
          from: location,
          forward: location,
          onError: () => {
            toast.error("An error occurred while continuing the process.");
            refreshLists();
          },
        });
      }
    } else {
      await startProcess({
        prescriptionId: prescription._id,
        from: location,
        forward: location,
        onError: () => {
          toast.error("An error occurred while starting the prescription.");
        },
      });
    }
  };

  const getLatestEngagementTimestamp = (prescription: any): number | null => {
    if (!Array.isArray(prescription.processes) || prescription.processes.length === 0) {
      return null;
    }
    let latest: number | null = null;
    for (const proc of prescription.processes) {
      // Backend returns lastUpdated/started as unix timestamps (ms)
      const ts = proc.lastUpdated || proc.started;
      if (ts && (!latest || ts > latest)) {
        latest = ts;
      }
    }
    return latest;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <SortableHead col="name" label={t("name")} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} className="w-[35%]" />
          <SortableHead col="status" label={t("status")} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} className="w-[100px]" />
          <SortableHead col="engagements" label={t("engagements")} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} className="w-[120px]" />
          <SortableHead col="recent" label={t("recent_engagement", "Last Activity")} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} className="w-[130px]" />
          <SortableHead col="folder" label={t("folder_column", "Folder")} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} className="w-[120px]" />
          <TableHead className="w-[100px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPrescriptions.map((prescription: any) => {
          const runningCount = Array.isArray(prescription.processes)
            ? prescription.processes.filter((p: any) => p.status === "RUNNING").length
            : 0;
          const isAuthor = prescription.isAuthor;
          const isEditor = prescription.isAuthor || prescription.isCollaborator;
          const folderName = prescription.metadata?.folderName;
          const folderColor = prescription.metadata?.folderColor;
          const latestTimestamp = getLatestEngagementTimestamp(prescription);
          const upcomingReminder = prescription.upcomingReminder;

          return (
            <TableRow
              key={prescription._id}
              className="h-12 group"
            >
              <TableCell className="py-2">
                <div className="flex items-center gap-2">
                  {onToggleFavorite && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(prescription._id);
                      }}
                      className="text-gray-300 hover:text-yellow-500 transition-colors flex-shrink-0"
                    >
                      <Star
                        className={`w-4 h-4 ${
                          prescription.metadata?.isFavorite
                            ? "fill-yellow-500 text-yellow-500"
                            : ""
                        }`}
                      />
                    </button>
                  )}
                  <span
                    className={`text-sm font-medium truncate ${
                      !prescription.valid
                        ? "text-gray-400"
                        : "text-gray-900"
                    }`}
                  >
                    {prescription.name}
                  </span>
                  {/* PWF badge — clickable link to PWF Hub */}
                  {(prescription as any).__isPwf && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        // For linked legacy prescriptions fiPwfId points to the PWF document;
                        // for native PWF items the item's own _id is the PWF id
                        const pwfId = (prescription as any).fiPwfId ?? prescription._id;
                        navigate(`/pwf?id=${pwfId}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const pwfId = (prescription as any).fiPwfId ?? prescription._id;
                          navigate(`/pwf?id=${pwfId}`);
                        }
                      }}
                      className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 border-violet-200 ml-1.5 shrink-0 cursor-pointer hover:bg-violet-200 transition-colors"
                      title="Open in PWF Hub"
                    >
                      PWF
                    </span>
                  )}
                  {/* Reminder indicator */}
                  {upcomingReminder && (() => {
                    const scheduledTime = DateTime.fromISO(
                      upcomingReminder.scheduledDateTime
                    ).setLocale(i18n.language);
                    const isPast = scheduledTime < DateTime.now();
                    return (
                      <Badge
                        variant="outline"
                        className={`text-[10px] py-0 px-1.5 flex-shrink-0 inline-flex items-center gap-1 ${
                          isPast
                            ? "border-orange-300 text-orange-700 bg-orange-50"
                            : "border-primary/20 text-primary bg-primary/5"
                        }`}
                      >
                        <Bell className="w-3 h-3 flex-shrink-0" />
                        {scheduledTime.toRelative({ locale: i18n.language })}
                      </Badge>
                    );
                  })()}
                </div>
              </TableCell>
              <TableCell className="py-2">
                {runningCount > 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    {t("in_progress")}
                  </span>
                ) : prescription.engagementCount > 0 ? (
                  <span className="text-xs text-gray-500">
                    {t("completed", "Completed")}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">
                    {t("not_started")}
                  </span>
                )}
              </TableCell>
              <TableCell className="py-2">
                <span className="text-sm text-gray-600">
                  {runningCount > 0 ? (
                    <>
                      <span className="font-medium text-green-700">{runningCount}</span>
                      <span className="text-gray-400"> / {prescription.engagementCount || 0}</span>
                    </>
                  ) : (
                    prescription.engagementCount || 0
                  )}
                </span>
              </TableCell>
              <TableCell className="py-2">
                {latestTimestamp ? (
                  <span className="text-xs text-gray-500">
                    {DateTime.fromMillis(latestTimestamp)
                      .setLocale(i18n.language)
                      .toRelative({ locale: i18n.language })}
                  </span>
                ) : (
                  <span className="text-xs text-gray-300">-</span>
                )}
              </TableCell>
              <TableCell className="py-2">
                {folderName ? (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: folderColor || "#6B7280" }}
                    />
                    <span className="text-xs text-gray-500 truncate">
                      {folderName}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-300">-</span>
                )}
              </TableCell>
              <TableCell className="py-2">
                <div className="flex items-center gap-1">
                  {/* Quick Start/Continue button */}
                  {prescription.valid && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartOrResume(prescription);
                      }}
                      title={runningCount > 0 ? t("continue_activity", "Continue") : t("action_start", "Start")}
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Quick Edit button */}
                  {isEditor && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-primary hover:bg-primary/5"
                      onClick={(e) => {
                        e.stopPropagation();
                        editPrescription(prescription._id);
                      }}
                      title={t("edit")}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {/* 3-dot menu for secondary actions */}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {/* Section 1: Primary actions */}
                      {prescription.valid && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => handleStartOrResume(prescription)}
                        >
                          <Play className="w-4 h-4 me-2" />
                          {t("action_start", "Start")}
                        </DropdownMenuItem>
                      )}
                      {!isMobile && isEditor && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => editPrescription(prescription._id)}
                        >
                          <Pencil className="w-4 h-4 me-2" />
                          {t("edit")}
                        </DropdownMenuItem>
                      )}

                      {/* Section 2: Organize */}
                      <DropdownMenuSeparator />
                      {auth.email && !isMobile && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => renamePrescription(prescription._id)}
                        >
                          <PenLine className="w-4 h-4 me-2" />
                          {t("rename")}
                        </DropdownMenuItem>
                      )}
                      {onMoveToFolder && folders && folders.length > 0 && (
                        <MoveToFolderMenu
                          folders={folders}
                          onMoveToFolder={(folderId) =>
                            onMoveToFolder(prescription._id, folderId)
                          }
                          currentFolderId={prescription.metadata?.folderId}
                        />
                      )}
                      {auth.email && prescription.valid && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/portal?tab=workspace&reminder=${prescription._id}`
                            )
                          }
                        >
                          <Bell className="w-4 h-4 me-2" />
                          {t("set_reminder")}
                        </DropdownMenuItem>
                      )}

                      {/* Section 3: Share & History */}
                      {auth.email && prescription.valid && (
                        <>
                          <DropdownMenuSeparator />
                          {isAuthor && (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => handleCopyLink(prescription._id)}
                            >
                              <Link className="w-4 h-4 me-2" />
                              {t("copy_link")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => viewPrescription(prescription._id, true)}
                          >
                            <Eye className="w-4 h-4 me-2" />
                            {t("engagement_history")}
                          </DropdownMenuItem>
                        </>
                      )}

                      {/* Section 4: More... submenu for secondary items */}
                      {auth.email && prescription.valid && !isMobile && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="cursor-pointer">
                              <MoreHorizontal className="w-4 h-4 me-2" />
                              {t("more_actions", "More...")}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {isEditor && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => {
                                    // Note: Edit details would require a modal state
                                    // For now, just show a toast
                                    toast.info(t("edit_details_note") || "Edit Details - Coming soon in list view");
                                  }}
                                >
                                  <ClipboardList className="w-4 h-4 me-2" />
                                  {t("edit_details") || "Edit Details"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() =>
                                  navigate(
                                    `/insights?wid=${encodeURIComponent(prescription._id.toString())}`
                                  )
                                }
                              >
                                <BarChart3 className="w-4 h-4 me-2" />
                                {t("analytics")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() =>
                                  navigate(
                                    `/examples/${prescription._id}`
                                  )
                                }
                              >
                                <BookOpen className="w-4 h-4 me-2" />
                                {t("view_examples") || "View Examples"}
                              </DropdownMenuItem>
                              {isAuthor && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() =>
                                    navigate(
                                      `/examples/${prescription._id}?manage=true`
                                    )
                                  }
                                >
                                  <Lightbulb className="w-4 h-4 me-2" />
                                  {t("manage_examples") || "Manage Examples"}
                                </DropdownMenuItem>
                              )}
                              {(isAuthor || prescription.isExpert) && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => managePermissions(prescription._id)}
                                >
                                  <Lock className="w-4 h-4 me-2" />
                                  {t("manage_permissions")}
                                </DropdownMenuItem>
                              )}
                              {prescription.isExpert && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => expertShareWorkflow(prescription._id)}
                                >
                                  <UserPlus className="w-4 h-4 me-2" />
                                  {t("share")}
                                </DropdownMenuItem>
                              )}
                              {isAuthor && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => exportPrescription(prescription._id)}
                                  >
                                    <Download className="w-4 h-4 me-2" />
                                    {t("export")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => copyPrescriptionAsExample(prescription._id)}
                                  >
                                    <Upload className="w-4 h-4 me-2" />
                                    {t("copy_activity_as_example")}
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(prescription.componentFlags?.data_event ||
                                prescription.componentFlags?.data_event_analysis) && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() =>
                                    navigate(
                                      `/dataEvent?wid=${encodeURIComponent(
                                        prescription._id.toString(),
                                      )}`,
                                    )
                                  }
                                >
                                  <Table2 className="w-4 h-4 me-2" />
                                  {t("data_event")}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </>
                      )}

                      {/* Section 5: Destructive — always last */}
                      {isAuthor && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => deletePrescription(prescription._id)}
                          >
                            {prescription.versions > 0 ? (
                              <>
                                <Archive className="w-4 h-4 me-2" />
                                {t("archive")}
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4 me-2" />
                                {t("delete")}
                              </>
                            )}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// ─── Sortable Column Header ──────────────────────────────────────────────────

const SortableHead: React.FC<{
  col: SortColumn;
  label: string;
  sortCol: SortColumn | null;
  sortDir: SortDir;
  onSort: (col: SortColumn) => void;
  className?: string;
}> = ({ col, label, sortCol, sortDir, onSort, className }) => {
  const isActive = sortCol === col;
  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors -ms-1 px-1 py-0.5 rounded"
      >
        {label}
        {isActive ? (
          sortDir === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />
        )}
      </button>
    </TableHead>
  );
};

export default ActivityListView;
